import React, { useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch } from "react-redux";
import {
  setEventPublished,
  fetchSessionsForEvent,
} from "../../../redux/features/adminEventsSlice";
import { zonedTimeToUtc, format } from "date-fns-tz";
import { formatInTimeZone } from "date-fns-tz";

const publishedAtDateText = ({ publishedAt, timezone }) => {
  const utcDateStart = zonedTimeToUtc(publishedAt?.toMillis() || 0, timezone);
  const datePattern = "EEEE, MMMM do, yyyy h:mm a z";
  return format(utcDateStart, datePattern, { timeZone: timezone });
};

const formatDate = (timestamp, timezone, pattern = "MMM do, yyyy h:mm a") => {
  if (!timestamp || !timestamp.toDate) {
    return "Not set";
  }
  return formatInTimeZone(timestamp.toDate(), timezone, pattern);
};

const SectionHeader = ({ title, isValid, issues = [] }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{ marginBottom: "10px" }}
    >
      <Typography variant="h6">{title}</Typography>
      {isValid && issues.length === 0 && (
        <CheckCircleIcon color="success" sx={{ fontSize: "20px" }} />
      )}
      {issues.length > 0 && (
        <WarningIcon color="warning" sx={{ fontSize: "20px" }} />
      )}
    </Box>
  );
};

const ValidationIssue = ({ message }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        marginTop: "8px",
        padding: "8px",
        backgroundColor: "warning.light",
        borderRadius: "4px",
      }}
    >
      <WarningIcon color="warning" sx={{ fontSize: "18px" }} />
      <Typography variant="body2" color="warning.dark">
        {message}
      </Typography>
    </Box>
  );
};

const EventPublishing = () => {
  const { event } = useOutletContext();
  const dispatch = useDispatch();
  const eventId = event?.id;

  // Fetch sessions on mount if not already loaded
  useEffect(() => {
    if (eventId) {
      dispatch(fetchSessionsForEvent({ eventId }));
    }
  }, [dispatch, eventId]);

  if (!event) {
    return;
  }
  const {
    id: eventIdFromEvent,
    publishedAt,
    timezone,
    startsAt,
    endsAt,
    ticketTypes = [],
    sessions = [],
    banners = [],
    name,
  } = event;

  // Use eventId from destructuring if eventId from optional chaining is undefined
  const finalEventId = eventId || eventIdFromEvent;

  // Validation for each section
  const eventDatesIssues = [];
  if (!startsAt || !endsAt) {
    eventDatesIssues.push("Event start or end date is not set");
  } else {
    // Check if start date is after end date
    const startMillis = startsAt.toMillis();
    const endMillis = endsAt.toMillis();
    if (startMillis >= endMillis) {
      eventDatesIssues.push("Event start date must be before the end date");
    }

    // Check if event is in the past
    const nowMillis = Date.now();
    if (startMillis < nowMillis) {
      eventDatesIssues.push("Event start date is in the past");
    }
    if (endMillis < nowMillis) {
      eventDatesIssues.push("Event end date is in the past");
    }
  }
  const eventDatesValid = eventDatesIssues.length === 0;

  const bannersIssues = [];
  if (!banners || banners.length === 0) {
    bannersIssues.push("No banners have been added to this event");
  }
  const bannersValid = bannersIssues.length === 0;

  const ticketTypesIssues = [];
  const ticketTypeIssuesMap = new Map(); // Map ticket type key to array of issues
  if (ticketTypes.length === 0) {
    ticketTypesIssues.push("No ticket types have been configured");
  } else {
    const eventStartMillis = startsAt?.toMillis();
    const eventEndMillis = endsAt?.toMillis();

    ticketTypes.forEach((ticketType) => {
      const {
        key,
        name: ticketTypeName,
        availabilityStartsAt,
        availabilityEndsAt,
      } = ticketType;
      const ticketTypeIssues = [];

      // Check if availability dates are set
      if (!availabilityStartsAt || !availabilityEndsAt) {
        const issue = `"${ticketTypeName}" is missing availability start or end date`;
        ticketTypeIssues.push(issue);
        ticketTypesIssues.push(issue);
      } else {
        const availabilityStartMillis = availabilityStartsAt.toMillis();
        const availabilityEndMillis = availabilityEndsAt.toMillis();

        // Check if availability start is after or equal to availability end
        if (availabilityStartMillis >= availabilityEndMillis) {
          const issue = `"${ticketTypeName}" availability start date must be before the end date`;
          ticketTypeIssues.push(issue);
          ticketTypesIssues.push(issue);
        }

        // Check availability window relative to event dates (if event dates are set).
        // Ticket sales often start before the event and can end before it (e.g. early-bird types).
        // We only flag availability that starts after the event ends (tickets would be unsellable).
        if (eventStartMillis && eventEndMillis) {
          if (availabilityStartMillis > eventEndMillis) {
            const issue = `"${ticketTypeName}" availability starts after event ends`;
            ticketTypeIssues.push(issue);
            ticketTypesIssues.push(issue);
          }
        }
      }

      if (ticketTypeIssues.length > 0) {
        ticketTypeIssuesMap.set(key, ticketTypeIssues);
      }
    });
  }
  const ticketTypesValid = ticketTypesIssues.length === 0;

  const sessionsIssues = [];
  const sessionIssuesMap = new Map(); // Map session ID to array of issues
  if (sessions && sessions.length > 0 && startsAt && endsAt) {
    const eventStartMillis = startsAt.toMillis();
    const eventEndMillis = endsAt.toMillis();

    sessions.forEach((session) => {
      const {
        id,
        name: sessionName,
        startsAt: sessionStartsAt,
        endsAt: sessionEndsAt,
      } = session;
      const sessionIssues = [];

      if (sessionStartsAt && sessionEndsAt) {
        const sessionStartMillis = sessionStartsAt.toMillis();
        const sessionEndMillis = sessionEndsAt.toMillis();

        if (sessionStartMillis < eventStartMillis) {
          const issue = `"${sessionName}" starts before event start time`;
          sessionIssues.push(issue);
          sessionsIssues.push(issue);
        }
        if (sessionEndMillis > eventEndMillis) {
          const issue = `"${sessionName}" ends after event end time`;
          sessionIssues.push(issue);
          sessionsIssues.push(issue);
        }
      } else if (!sessionStartsAt || !sessionEndsAt) {
        const issue = `"${sessionName}" is missing start or end time`;
        sessionIssues.push(issue);
        sessionsIssues.push(issue);
      }

      if (sessionIssues.length > 0) {
        sessionIssuesMap.set(id, sessionIssues);
      }
    });
  }
  const sessionsValid = sessionsIssues.length === 0;

  const hasWarnings =
    eventDatesIssues.length > 0 ||
    bannersIssues.length > 0 ||
    ticketTypesIssues.length > 0 ||
    sessionsIssues.length > 0;

  return (
    <Box sx={{ paddingBottom: "40px" }}>
      <Typography variant="h2" paddingTop={"20px"}>
        Publish
      </Typography>
      {publishedAt ? (
        <Box>
          <Typography>
            {`Event published ${publishedAtDateText({
              publishedAt,
              timezone,
            })}`}{" "}
          </Typography>
          <Button
            sx={{ marginTop: "20px" }}
            onClick={() => {
              dispatch(
                setEventPublished({ eventId: finalEventId, published: false }),
              );
            }}
          >
            Unpublish Event
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography
            variant="h6"
            sx={{ marginTop: "20px", marginBottom: "20px" }}
          >
            Event Configuration Review
          </Typography>

          {/* Event Dates */}
          <Paper elevation={2} sx={{ padding: "20px", marginBottom: "20px" }}>
            <SectionHeader
              title="Event Dates"
              isValid={eventDatesValid}
              issues={eventDatesIssues}
            />
            <Box sx={{ marginBottom: "10px" }}>
              <Typography variant="body2" color="text.secondary">
                Event Name: {name || "Not set"}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Typography variant="body2" color="text.secondary">
                Start: {startsAt ? formatDate(startsAt, timezone) : "Not set"}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Typography variant="body2" color="text.secondary">
                End: {endsAt ? formatDate(endsAt, timezone) : "Not set"}
              </Typography>
            </Box>
            {eventDatesIssues.map((issue, index) => (
              <ValidationIssue key={index} message={issue} />
            ))}
          </Paper>

          {/* Banners */}
          <Paper elevation={2} sx={{ padding: "20px", marginBottom: "20px" }}>
            <SectionHeader
              title="Banners"
              isValid={bannersValid}
              issues={bannersIssues}
            />
            <Typography variant="body2" color="text.secondary">
              {banners.length === 0
                ? "No banners added"
                : `${banners.length} banner${
                    banners.length === 1 ? "" : "s"
                  } added`}
            </Typography>
            {bannersIssues.map((issue, index) => (
              <ValidationIssue key={index} message={issue} />
            ))}
          </Paper>

          {/* Ticket Types */}
          <Paper elevation={2} sx={{ padding: "20px", marginBottom: "20px" }}>
            <SectionHeader
              title={`Ticket Types (${ticketTypes.length})`}
              isValid={ticketTypesValid}
              issues={ticketTypesIssues}
            />
            {ticketTypes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No ticket types configured
              </Typography>
            ) : (
              <List dense>
                {ticketTypes.map((ticketType) => {
                  const {
                    key,
                    name: ticketTypeName,
                    availabilityStartsAt,
                    availabilityEndsAt,
                  } = ticketType;
                  const ticketTypeIssuesForThis =
                    ticketTypeIssuesMap.get(key) || [];
                  const hasIssue = ticketTypeIssuesForThis.length > 0;
                  return (
                    <ListItem
                      key={key}
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        backgroundColor: hasIssue
                          ? "warning.light"
                          : "transparent",
                        borderRadius: hasIssue ? "4px" : "0",
                        marginBottom: "16px",
                        padding: hasIssue ? "8px" : "0",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Link
                            to={`/events/${finalEventId}/ticketTypes/${key}`}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            {ticketTypeName || `Ticket Type ${key}`}
                          </Link>
                        }
                      />
                      <Box sx={{ marginTop: "2px" }}>
                        <Typography variant="caption" display="block">
                          Available:{" "}
                          {availabilityStartsAt && availabilityEndsAt
                            ? `${formatDate(
                                availabilityStartsAt,
                                timezone,
                                "MMM do, h:mm a",
                              )} - ${formatDate(
                                availabilityEndsAt,
                                timezone,
                                "MMM do, h:mm a",
                              )}`
                            : "Not set"}
                        </Typography>
                      </Box>
                      {ticketTypeIssuesForThis.map((issue, index) => (
                        <ValidationIssue key={index} message={issue} />
                      ))}
                    </ListItem>
                  );
                })}
              </List>
            )}
            {ticketTypesIssues
              .filter((issue) => !issue.includes('"'))
              .map((issue, index) => (
                <ValidationIssue key={index} message={issue} />
              ))}
          </Paper>

          {/* Sessions */}
          <Paper elevation={2} sx={{ padding: "20px", marginBottom: "20px" }}>
            <SectionHeader
              title={`Sessions (${sessions?.length || 0})`}
              isValid={sessionsValid}
              issues={sessionsIssues}
            />
            {!sessions || sessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sessions configured
              </Typography>
            ) : (
              <List dense>
                {sessions.map((session) => {
                  const {
                    id,
                    name: sessionName,
                    startsAt: sessionStartsAt,
                    endsAt: sessionEndsAt,
                  } = session;
                  const sessionIssuesForThisSession =
                    sessionIssuesMap.get(id) || [];
                  const hasIssue = sessionIssuesForThisSession.length > 0;
                  return (
                    <ListItem
                      key={id}
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        backgroundColor: hasIssue
                          ? "warning.light"
                          : "transparent",
                        borderRadius: hasIssue ? "4px" : "0",
                        marginBottom: "16px",
                        padding: hasIssue ? "8px" : "0",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Link
                            to={`/events/${finalEventId}/sessions/${id}`}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            {sessionName || `Session ${id}`}
                          </Link>
                        }
                      />
                      <Box sx={{ marginTop: "2px" }}>
                        <Typography variant="caption" display="block">
                          {sessionStartsAt && sessionEndsAt
                            ? `${formatDate(
                                sessionStartsAt,
                                timezone,
                                "MMM do, h:mm a",
                              )} - ${formatDate(
                                sessionEndsAt,
                                timezone,
                                "MMM do, h:mm a",
                              )}`
                            : "Dates not set"}
                        </Typography>
                      </Box>
                      {sessionIssuesForThisSession.map((issue, index) => (
                        <ValidationIssue key={index} message={issue} />
                      ))}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>

          {/* Warning Alert */}
          {hasWarnings && (
            <Alert severity="warning" sx={{ marginBottom: "20px" }}>
              Please review the warnings above before publishing. The event may
              not function correctly with these issues.
            </Alert>
          )}

          <Button
            variant="contained"
            sx={{ marginTop: "20px" }}
            onClick={() => {
              dispatch(
                setEventPublished({ eventId: finalEventId, published: true }),
              );
            }}
          >
            Publish Event
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EventPublishing;
